import * as THREE from 'three';
import { GameObject, ObjectType } from '../GameObject';

export class StaticObject extends GameObject {
  // Instancing-related properties
  protected static instancedMeshes: Map<string, THREE.InstancedMesh> = new Map();
  protected static scene: THREE.Scene | null = null;
  protected instanceId: number = -1;
  protected instanceType: string = '';
  
  constructor(name: string) {
    // Static objects automatically have STATIC type
    super(name, ObjectType.STATIC);
  }
  
  /**
   * Set the scene to add instanced meshes to
   */
  public static setScene(scene: THREE.Scene): void {
    this.scene = scene;
    
    // Add any existing instanced meshes to the scene
    if (this.scene !== null) {
      this.instancedMeshes.forEach(mesh => {
        if (!mesh.parent) {
          this.scene!.add(mesh);
        }
      });
    }
  }
  
  // Override standard mesh handling to work with instanced meshes
  public setMesh(mesh: THREE.Mesh): void {
    // Only set mesh directly if not using instancing
    if (this.instanceId === -1) {
      super.setMesh(mesh);
    }
  }
  
  // Method to set up instancing
  public setInstance(instanceType: string, instanceId: number): void {
    this.instanceType = instanceType;
    this.instanceId = instanceId;
    
    // Update instance matrix based on object transform
    this.updateInstanceMatrix();
  }
  
  // Update the instance matrix when transform changes
  protected updateInstanceMatrix(): void {
    if (this.instanceId === -1 || !this.instanceType) return;
    
    const instancedMesh = StaticObject.instancedMeshes.get(this.instanceType);
    if (!instancedMesh) return;
    
    // Create matrix from position, rotation, and scale
    const matrix = new THREE.Matrix4();
    const position = this.getPosition();
    const rotation = this.getRotation();
    const scale = this.getScale();
    
    matrix.compose(position, new THREE.Quaternion().setFromEuler(rotation), scale);
    instancedMesh.setMatrixAt(this.instanceId, matrix);
    instancedMesh.instanceMatrix.needsUpdate = true;
  }
  
  // Override transform methods to update instance matrix
  public setPosition(x: number, y: number, z: number): void {
    super.setPosition(x, y, z);
    this.updateInstanceMatrix();
  }
  
  public setRotation(x: number, y: number, z: number): void {
    super.setRotation(x, y, z);
    this.updateInstanceMatrix();
  }
  
  public setScale(x: number, y: number, z: number): void {
    super.setScale(x, y, z);
    this.updateInstanceMatrix();
  }
  
  // Static method to create or get instanced mesh
  public static getInstancedMesh(
    type: string, 
    geometry: THREE.BufferGeometry, 
    material: THREE.Material, 
    count: number
  ): THREE.InstancedMesh {
    if (!this.instancedMeshes.has(type)) {
      const instancedMesh = new THREE.InstancedMesh(geometry, material, count);
      instancedMesh.castShadow = true;
      instancedMesh.receiveShadow = true;
      
      // Set instancedMesh to frustumCulled = true to improve performance
      // when instances are outside the camera view
      instancedMesh.frustumCulled = true;
      
      this.instancedMeshes.set(type, instancedMesh);
      
      // Add to scene if available
      if (this.scene !== null) {
        this.scene.add(instancedMesh);
      }
    }
    
    return this.instancedMeshes.get(type) as THREE.InstancedMesh;
  }
}
